const API_BASE_URL = process.env.REACT_APP_TEMPLATE_BASE_URL || process.env.REACT_APP_REST_BASE_URL || "";

const VISITOR_KEY = "tt_web_visitor_id";
const SESSION_KEY = "tt_web_session_id";
const JOURNEY_ID_KEY = "tt_web_journey_id";
const JOURNEY_STARTED_AT_KEY = "tt_web_journey_started_at";
const JOURNEY_VERSION_KEY = "tt_web_journey_version";
const LANDING_PAGE_KEY = "tt_web_landing_page";
const JOURNEY_KEY = "tt_web_journey";
const MAX_JOURNEY_EVENTS = 100;
const JOURNEY_VERSION = "2";

const createId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const value = Math.floor(Math.random() * 16);
    return (character === "x" ? value : (value & 0x3) | 0x8).toString(16);
  });
};

const storedId = (storage, key) => {
  try {
    let value = storage.getItem(key);
    if (!value) {
      value = createId();
      storage.setItem(key, value);
    }
    return value;
  } catch {
    return createId();
  }
};

const currentPage = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;
const navigatedPage = () => `${window.location.pathname}${window.location.search}`;

const ensureJourneyState = () => {
  try {
    if (sessionStorage.getItem(JOURNEY_VERSION_KEY) === JOURNEY_VERSION) return;
    sessionStorage.removeItem(JOURNEY_KEY);
    sessionStorage.removeItem(LANDING_PAGE_KEY);
    sessionStorage.removeItem(JOURNEY_ID_KEY);
    sessionStorage.removeItem(JOURNEY_STARTED_AT_KEY);
    sessionStorage.setItem(JOURNEY_VERSION_KEY, JOURNEY_VERSION);
  } catch {
    // Tracking continues with an in-memory identity if storage is unavailable.
  }
};

const readJourney = () => {
  ensureJourneyState();
  try {
    const journey = JSON.parse(sessionStorage.getItem(JOURNEY_KEY));
    return Array.isArray(journey) ? journey : [];
  } catch {
    return [];
  }
};

const writeJourney = (journey) => {
  try {
    sessionStorage.setItem(JOURNEY_KEY, JSON.stringify(journey.slice(-MAX_JOURNEY_EVENTS)));
  } catch {
    // The conversion payload still includes the events available in memory.
  }
};

const identity = () => {
  ensureJourneyState();
  const journey_id = storedId(sessionStorage, JOURNEY_ID_KEY);
  let journey_started_at;
  try {
    journey_started_at = sessionStorage.getItem(JOURNEY_STARTED_AT_KEY);
    if (!journey_started_at) {
      journey_started_at = new Date().toISOString();
      sessionStorage.setItem(JOURNEY_STARTED_AT_KEY, journey_started_at);
    }
  } catch {
    journey_started_at = new Date().toISOString();
  }
  return {
    visitor_id: storedId(localStorage, VISITOR_KEY),
    session_id: storedId(sessionStorage, SESSION_KEY),
    journey_id,
    journey_started_at,
  };
};

const addJourneyEvent = (event_type, { page = currentPage(), cta = null } = {}) => {
  // Establish the journey boundary before timestamping its first event.
  identity();
  landingPage();
  const journey = readJourney();
  const now = new Date();
  const previous = journey[journey.length - 1];

  // React Strict Mode may invoke the initial page effect twice in development.
  // Avoid duplicate consecutive events while retaining genuine repeat visits.
  if (
    previous &&
    previous.event_type === event_type &&
    previous.page === page &&
    previous.cta === cta &&
    now.getTime() - new Date(previous.occurred_at).getTime() < 1000
  ) {
    return previous;
  }

  const event = {
    event_id: createId(),
    event_type,
    page,
    cta,
    occurred_at: now.toISOString(),
  };
  const nextJourney = [...journey, event].slice(-MAX_JOURNEY_EVENTS);
  writeJourney(nextJourney);
  return event;
};

const landingPage = () => {
  try {
    let page = sessionStorage.getItem(LANDING_PAGE_KEY);
    if (!page) {
      page = navigatedPage();
      sessionStorage.setItem(LANDING_PAGE_KEY, page);
    }
    return page;
  } catch {
    return currentPage();
  }
};

export const trackPageVisit = () => {
  const page = navigatedPage();
  const latestPage = [...readJourney()].reverse().find((event) => event.event_type === "page_view")?.page;
  // Returning from the thank-you page starts a fresh acquisition journey.
  if (page === "/" && latestPage === "/demo-thankyou") completeJourney();
  return addJourneyEvent("page_view", { page });
};

export const trackCtaClick = (cta) => {
  const label = typeof cta === "string"
    ? cta.replace(/[→←]/g, "").trim().replace(/\s+/g, " ").slice(0, 200)
    : "";
  if (label) addJourneyEvent("cta_click", { cta: label });
};

export const trackModalOpen = (cta = "Request a Demo") =>
  addJourneyEvent("modal_open", { cta });

export const createTrackingContext = ({ cta, organization, useLatestCta = false } = {}) => {
  const { visitor_id, session_id, journey_id, journey_started_at } = identity();
  const latestCta = [...readJourney()].reverse().find((event) => event.event_type === "cta_click")?.cta;
  return {
    event_id: createId(),
    visitor_id,
    session_id,
    journey_id,
    journey_started_at,
    conversion_at: new Date().toISOString(),
    landing_page: landingPage(),
    conversion_page: currentPage(),
    cta: (useLatestCta ? latestCta : null) || cta || "Unknown",
    organization: organization?.trim() || null,
    web_track: readJourney(),
  };
};

// A successful lead ends one journey. The next interaction in the same tab
// starts a fresh journey instead of inheriting historical navigation.
export const completeJourney = () => {
  try {
    sessionStorage.removeItem(JOURNEY_KEY);
    sessionStorage.removeItem(LANDING_PAGE_KEY);
    sessionStorage.setItem(JOURNEY_ID_KEY, createId());
    sessionStorage.setItem(JOURNEY_STARTED_AT_KEY, new Date().toISOString());
    sessionStorage.setItem(JOURNEY_VERSION_KEY, JOURNEY_VERSION);
  } catch {
    // Storage may be unavailable; a new ID is still created on the next call.
  }
};

export const saveCookieConsent = async ({ analytics, marketing, action }) => {
  const response = await fetch(`${API_BASE_URL}/tracking/consent`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      analytics: Boolean(analytics),
      marketing: Boolean(marketing),
      action,
      tracking: createTrackingContext({ cta: "Cookie Banner" }),
    }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || "Unable to save cookie consent");
  }
  return response.json();
};

export const submitExitPopup = async (payload) => {
  const response = await fetch(`${API_BASE_URL}/contact/exit-popup-submit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || "Unable to submit your request");
  }
  return response.json();
};
